import { useCallback, useEffect, useState } from 'react';
import { bottleMessagesApiUrl, nonce } from '../utils/runtime';
import { XP_EVENTS } from './useXpBonus';

const API_URL = bottleMessagesApiUrl || '/wp-json/pfandpirat/v1/bottle-messages';

async function parseJsonResponse(response) {
  const rawText = await response.text();

  if (!rawText) {
    return null;
  }

  try {
    return JSON.parse(rawText);
  } catch (error) {
    throw new Error('Server-Antwort ist kein sauberes JSON.');
  }
}

export function useBottleMessages({ applyGamificationPayload } = {}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyMessageId, setBusyMessageId] = useState(null);
  const [openedMessages, setOpenedMessages] = useState({});

  const loadMessages = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetch(API_URL, {
        credentials: 'include',
        headers: {
          'X-WP-Nonce': nonce,
        },
      });

      const data = await parseJsonResponse(res);

      if (!res.ok) {
        throw new Error(data?.message || 'Flaschenposten konnten nicht geladen werden.');
      }

      const normalized = Array.isArray(data) ? data : [];
      setMessages(normalized);
      return normalized;
    } catch (error) {
      console.warn('Bottle messages load failed:', error);
      setMessages([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const openMessage = useCallback(
    async (message) => {
      const messageId = Number(message?.id || 0);
      if (!messageId || busyMessageId) {
        return null;
      }

      setBusyMessageId(messageId);

      try {
        const res = await fetch(`${API_URL}/${messageId}/open`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
          },
        });

        const data = await parseJsonResponse(res);

        if (!res.ok || !data?.success) {
          throw new Error(data?.message || 'Flaschenpost konnte nicht geöffnet werden.');
        }

        if (Number(data?.awarded || 0) !== 0 && typeof applyGamificationPayload === 'function') {
          applyGamificationPayload(data, {
            emitXpToast: true,
            event: data?.event || XP_EVENTS.OPEN_BOTTLE_MESSAGE,
            awarded: Number(data?.awarded || 0),
            toastLabel: data?.xp_toast?.label || 'Flaschenpost geöffnet',
          });
        }

        setOpenedMessages((prev) => ({
          ...prev,
          [messageId]: data,
        }));

        setMessages((prev) =>
          prev.map((entry) =>
            Number(entry?.id) === messageId
              ? {
                  ...entry,
                  has_opened: true,
                  opened_count: Number(entry?.opened_count || 0) + (data?.first_open ? 1 : 0),
                }
              : entry
          )
        );

        return data;
      } catch (error) {
        const failed = {
          success: false,
          error: error?.message || 'Flaschenpost konnte nicht geöffnet werden.',
        };

        setOpenedMessages((prev) => ({
          ...prev,
          [messageId]: failed,
        }));

        return failed;
      } finally {
        setBusyMessageId(null);
      }
    },
    [applyGamificationPayload, busyMessageId]
  );

  return {
    messages,
    loading,
    busyMessageId,
    openedMessages,
    loadMessages,
    openMessage,
  };
}

export default useBottleMessages;
